import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { CallbackResult, CallbackResultStatus, generateCsvDownload, getISO8601DateString } from '../../../../../utils';
import { loadApiExport } from '../../../../../models/exports/utils';
import { ListResponse } from '../../../../../models/list-response';

/**
 * A shared download modal component for all API collections.
 */
@Component({
  selector: 'app-generic-report-download-modal',
  templateUrl: './api-collection-export-modal.component.html',
  styleUrls: ['./api-collection-export-modal.component.css']
})
export class ApiCollectionExportModalComponent<Model> implements OnInit, OnDestroy {

  @Input() getPage: (pageNum: number) => Promise<ListResponse<Model>>;
  @Input() getContentItem: (item: Model, columnName: string) => string;
  @Input() totalNumPages: number;
  @Input() savedCallback: (result: CallbackResult) => undefined;
  @Input() columnNames: string[];
  @Input() columnHelpText: {[key: string]: string} = {};
  @Input() exportNameHumanReadable: string;
  @Input() exportFilenamePrefix: string;

  public loadingDownload = false;
  public serverError: number = null;

  // Used to halt any in-progress downloads when the modal is closed.
  public modalClosed = false;

  // Used to display the loading bar while downloading.
  public pageNum: number;

  public defaultDownloadFileName: string;
  public downloadFileName: string;
  // Whether each COLUMN_NAMES item has been selected for inclusion in the
  // download.
  public selectedColumns: {[key: string]: boolean} = {};

  constructor(
    public activeModal: NgbActiveModal
  ) {}

  ngOnInit() {
    this.defaultDownloadFileName = this.exportFilenamePrefix + '_Export_' + getISO8601DateString(new Date()) + '.csv';
    this.downloadFileName = this.defaultDownloadFileName;

    for (const columnName of this.columnNames) {
      this.selectedColumns[columnName] = false;
    }
  }

  ngOnDestroy() {
    this.modalClosed = true;
  }

  public handleSubmit() {
    if (!this.downloadFileName) {
      this.downloadFileName = this.defaultDownloadFileName;
    }

    this.pageNum = 0;

    this.loadingDownload = true;

    loadApiExport<Model>(
      this.getSelectedColumnsList(),
      (pageNum) => {
        this.pageNum = pageNum;
        return this.getPageInternal(pageNum);
      },
      this.getContentItem,
      () => this.modalClosed
    )
      .then(content => {
        // Do not generate the CSV if the modal has been closed.
        if (this.modalClosed) {
          return;
        }

        generateCsvDownload(content, this.downloadFileName);

        this.savedCallback({
            text: this.exportNameHumanReadable + ' export downloaded successfully.',
            status: CallbackResultStatus.success
          });
        this.activeModal.close();
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingDownload = false; });
  }

  public noColumnsAreSelected() {
    for (const columnName of this.columnNames) {
      if (this.selectedColumns[columnName]) {
        return false;
      }
    }

    return true;
  }

  public allColumnsAreSelected() {
    for (const columnName of this.columnNames) {
      if (!this.selectedColumns[columnName]) {
        return false;
      }
    }

    return true;
  }

  public setAllColumnsSelected(isSelected: boolean) {
    this.columnNames.forEach(columnName => {
      this.selectedColumns[columnName] = isSelected;
    });
  }

  public getDownloadProgressPercentageStr() {
    return Math.ceil((this.pageNum / this.totalNumPages) * 100).toString();
  }

  /**
   * Get the selected columns as a list.
   */
  public getSelectedColumnsList(): string[] {
    const selectedColumnsList: string[] = [];
    for (const columnName of this.columnNames) {
      if (this.selectedColumns[columnName]) {
        selectedColumnsList.push(columnName);
      }
    }

    return selectedColumnsList;
  }

  /**
   * This function allows getPage to be overridden by components which inherit
   * this one, such as CustomerExportModalComponent.
   */
  public getPageInternal(pageNum: number): Promise<ListResponse<Model>> {
    return this.getPage(pageNum);
  }

}
